/**
 * Index local des contacts vus dans les mails — alimente l'autocomplete
 * du SearchBar (`from:c…` → suggestions d'emails commençant par "c").
 *
 * Source : extrait From + To des headers de chaque message rendu dans
 * `hydrateRow` (sync.ts). Pas d'appel à People API (= pas de scope
 * supplémentaire requis), index purement local.
 *
 * Stockage : IndexedDB via idb-keyval. Persisté en debounce (1.5 s)
 * pour ne pas saturer IDB pendant un loadInbox qui ingère 200 contacts
 * d'un coup.
 *
 * Reactivité : `$state` deeply tracks → `byEmail[email] = {...}` est
 * réactif côté composants Svelte qui le consomment.
 */

import { get, set, del } from "idb-keyval";

export interface Contact {
  email: string;
  name: string;
  /** Unix ms du dernier mail vu. Utilisé pour ranking récence. */
  lastSeen: number;
  /** Nombre d'occurrences vues. Tie-break du sort. */
  count: number;
}

interface ContactsState {
  byEmail: Record<string, Contact>;
  loaded: boolean;
}

export const contactsState = $state<ContactsState>({
  byEmail: {},
  loaded: false,
});

const KEY = "mailfox:contacts:v1";

/** Hydrate depuis IDB au boot. Idempotent. */
export async function loadContacts(): Promise<void> {
  if (contactsState.loaded) return;
  try {
    const raw = await get<Contact[]>(KEY);
    if (raw) {
      const obj: Record<string, Contact> = {};
      for (const c of raw) obj[c.email] = c;
      contactsState.byEmail = obj;
    }
  } catch (e) {
    console.warn("loadContacts failed:", e);
  }
  contactsState.loaded = true;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function persistDebounced(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    saveTimer = null;
    try {
      await set(KEY, Object.values(contactsState.byEmail));
    } catch (e) {
      console.warn("contacts persist failed:", e);
    }
  }, 1500);
}

/** Validateur grossier d'un email : juste un `@` et pas vide. */
function looksLikeEmail(s: string): boolean {
  return s.length > 3 && s.includes("@") && !s.includes(" ");
}

/**
 * Ajoute ou update un contact. Idempotent — si déjà présent, incrémente
 * le count, met à jour lastSeen, complète le name si manquant.
 */
export function ingestContact(
  email: string | undefined | null,
  name: string | undefined | null,
  when: number,
): void {
  if (!email) return;
  const e = email.toLowerCase().trim();
  if (!looksLikeEmail(e)) return;
  const existing = contactsState.byEmail[e];
  if (existing) {
    existing.count++;
    if (when > existing.lastSeen) existing.lastSeen = when;
    if (!existing.name && name) existing.name = name.trim();
  } else {
    contactsState.byEmail[e] = {
      email: e,
      name: (name ?? "").trim(),
      lastSeen: when,
      count: 1,
    };
  }
  persistDebounced();
}

/**
 * Search par partial (substring) sur email + name. Ranking :
 *   1. prefix match (email ou name) en premier
 *   2. récence × fréquence (lastSeen desc, count desc)
 * Retourne top N (défaut 6).
 */
export function searchContacts(partial: string, limit = 6): Contact[] {
  const p = partial.toLowerCase().trim();
  if (!p) return [];
  const all = Object.values(contactsState.byEmail);
  const matches = all.filter(
    (c) =>
      c.email.toLowerCase().includes(p) ||
      c.name.toLowerCase().includes(p),
  );
  matches.sort((a, b) => {
    const aPrefix =
      a.email.toLowerCase().startsWith(p) ||
      a.name.toLowerCase().startsWith(p);
    const bPrefix =
      b.email.toLowerCase().startsWith(p) ||
      b.name.toLowerCase().startsWith(p);
    if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
    return b.lastSeen - a.lastSeen || b.count - a.count;
  });
  return matches.slice(0, limit);
}

/** Reset complet — appelé au sign-out. */
export async function clearContacts(): Promise<void> {
  contactsState.byEmail = {};
  contactsState.loaded = false;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  try {
    await del(KEY);
  } catch (e) {
    console.warn("clearContacts failed:", e);
  }
}

/**
 * Parse un header `To:` / `Cc:` qui peut contenir plusieurs adresses
 * séparées par des virgules. Retourne un tableau de {name, email}.
 *
 * Limitation : split naïf sur `,` — ne gère pas correctement les
 * quoted display names contenant une virgule ("Smith, John" <…>).
 * Acceptable v1 : ces cas sont rares et les contacts seront simplement
 * ratés (pas d'erreur).
 */
export function parseAddressList(raw: string): Array<{ name: string; email: string }> {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => {
      const trimmed = s.trim();
      const m = trimmed.match(/^"?([^"<]*?)"?\s*<([^>]+)>$/);
      if (m) return { name: m[1].trim(), email: m[2].trim() };
      return { name: "", email: trimmed };
    })
    .filter((a) => a.email && a.email.includes("@"));
}
